import { useState, useMemo } from 'react'

/**
 * Pagination hook that manages page state and extracts pagination data from API responses
 *
 * @param initialPage - Initial page number (default: 1)
 * @returns Pagination state and helper functions
 *
 * @example
 * ```tsx
 * const { page, goToPage, nextPage, prevPage, ...pagination } = usePagination()
 * const { data, isLoading } = useAccounts({ search, page })
 * const { items, total, currentPage, limit, totalPages, hasNextPage, hasPrevPage } =
 *   pagination.extract(data)
 *
 * return (
 *   <div>
 *     <Table data={items} />
 *     <Pagination
 *       currentPage={currentPage}
 *       totalPages={totalPages}
 *       onPageChange={goToPage}
 *       hasNext={hasNextPage}
 *       hasPrev={hasPrevPage}
 *     />
 *   </div>
 * )
 * ```
 */
export function usePagination(initialPage = 1) {
  const [page, setPage] = useState(initialPage)

  /**
   * Navigate to a specific page
   */
  const goToPage = (newPage: number) => {
    setPage(Math.max(1, newPage))
  }

  /**
   * Navigate to the next page
   */
  const nextPage = () => {
    setPage((prev) => prev + 1)
  }

  /**
   * Navigate to the previous page
   */
  const prevPage = () => {
    setPage((prev) => Math.max(1, prev - 1))
  }

  /**
   * Navigate to the first page
   */
  const firstPage = () => {
    setPage(1)
  }

  /**
   * Navigate to the last page
   */
  const lastPage = (totalPages: number) => {
    setPage(Math.max(1, totalPages))
  }

  /**
   * Reset page to initial value
   */
  const reset = () => {
    setPage(initialPage)
  }

  /**
   * Extract pagination data from API response
   * Supports Format A: { data: [], total: number, page: number, limit: number, stats?: any }
   */
  const extract = <T>(
    response:
      | {
          data?: T[]
          total?: number
          page?: number
          limit?: number
          stats?: any
        }
      | undefined
  ) => {
    const items = response?.data ?? []
    const total = response?.total ?? 0
    const currentPage = response?.page ?? 1
    const limit = response?.limit ?? 25
    const totalPages = Math.ceil(total / limit)
    const hasNextPage = currentPage < totalPages
    const hasPrevPage = currentPage > 1
    const stats = response?.stats ?? null

    return {
      items,
      total,
      currentPage,
      limit,
      totalPages,
      hasNextPage,
      hasPrevPage,
      stats,
    }
  }

  return {
    page,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    reset,
    extract,
  }
}

/**
 * Hook for managing pagination state and computing pagination info
 * Alternative to usePagination when you don't need the extract helper
 *
 * @param apiResponse - API response with pagination data
 * @param initialPage - Initial page number (default: 1)
 * @returns Pagination state, computed values, and helper functions
 *
 * @example
 * ```tsx
 * const [page, setPage] = useState(1)
 * const { data, isLoading } = useAccounts({ search, page })
 * const pagination = usePaginationState(data, page)
 *
 * return (
 *   <div>
 *     <Table data={pagination.items} />
 *     <Pagination
 *       currentPage={pagination.currentPage}
 *       totalPages={pagination.totalPages}
 *       onPageChange={setPage}
 *       hasNext={pagination.hasNextPage}
 *       hasPrev={pagination.hasPrevPage}
 *     />
 *   </div>
 * )
 * ```
 */
export function usePaginationState<T>(
  apiResponse:
    | {
        data?: T[]
        total?: number
        page?: number
        limit?: number
        stats?: any
      }
    | undefined,
  currentPageParam: number
) {
  const items = apiResponse?.data ?? []
  const total = apiResponse?.total ?? 0
  const currentPage = apiResponse?.page ?? currentPageParam
  const limit = apiResponse?.limit ?? 25
  const totalPages = Math.ceil(total / limit)
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1
  const stats = apiResponse?.stats ?? null

  return useMemo(
    () => ({
      items,
      total,
      currentPage,
      limit,
      totalPages,
      hasNextPage,
      hasPrevPage,
      stats,
    }),
    [items, total, currentPage, limit, totalPages, hasNextPage, hasPrevPage, stats]
  )
}

/**
 * Calculate pagination info from raw values
 * Useful when you have total and limit but don't have a full API response
 *
 * @param total - Total number of items
 * @param currentPage - Current page number
 * @param limit - Items per page
 * @returns Computed pagination values
 *
 * @example
 * ```tsx
 * const info = getPaginationInfo(150, 2, 25)
 * // Returns: { totalPages: 6, hasNextPage: true, hasPrevPage: true, startIndex: 25, endIndex: 49 }
 * ```
 */
export function getPaginationInfo(total: number, currentPage: number, limit: number) {
  const totalPages = Math.ceil(total / limit)
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1
  const startIndex = (currentPage - 1) * limit
  const endIndex = Math.min(startIndex + limit - 1, total - 1)

  return {
    totalPages,
    hasNextPage,
    hasPrevPage,
    startIndex,
    endIndex,
  }
}
